import { RefObject, useLayoutEffect, useState } from 'react';

import { useMuiSwitchableTheme } from '../../theme';

/**
 * Nearest ancestor that scrolls, if any. Measuring against the window is only
 * right when the page owns the viewport — inside a scrolling shell
 * (TabbedContainer's panel, say) the window is taller than the space there
 * actually is.
 * @param start - The element to look up from
 * @returns The scrolling ancestor, or null when the document is the scroller
 */
export const findScrollParent = (start: HTMLElement): HTMLElement | null => {
  let node = start.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Whether the DOCUMENT is what scrolls under `ref`: true on a phone, unless
 * the element sits inside a host that scrolls its own panel (a dialog, a
 * drawer, TabbedContainer), where the document doesn't move. The one answer
 * for every page chassis that pins itself to the screen under page scroll.
 * @param ref - The page's root element
 * @returns Whether to lay out for document scroll
 */
export const usePageScroll = (ref: RefObject<HTMLElement | null>): boolean => {
  const { isMobile: isPhone } = useMuiSwitchableTheme();
  const [insideScroller, setInsideScroller] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    setInsideScroller(!!isPhone && !!el && !!findScrollParent(el));
  }, [isPhone, ref]);
  return isPhone && !insideScroller;
};
